/**
 * usePortfolioAggregate — fetch a portfolio intensity rollup for a boundary
 * level (state | county | cbsa). Returns null when not built yet so the
 * caller can fall back to raster heatmap or markers.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthProvider";
import { fetchWithAuth } from "../../../api/http";

export type AggregationLevel = "state" | "county" | "cbsa";

export interface AggregateFeatureProperties {
  id?: string;
  name?: string;
  STUSPS?: string;
  STATEFP?: string;
  value_sum: number;
  share_pct: number;
  rank: number;
  cell_count: number;
  area_km2?: number | null;
  intensity?: number | null;
  /** Plan Harris Phase A: DALYs attributable to this polygon, computed
   * as share_pct/100 × (portfolio or project PM-category total DALYs).
   * Null when the backend didn't have calibration data (older runs). */
  dalys?: number | null;
}

export interface AggregateFeatureCollection {
  type: "FeatureCollection";
  metadata?: {
    level: AggregationLevel;
    portfolio_raster_total: number;
    feature_count: number;
    nonzero_count: number;
    /** Sum of per-project PM-category DALYs across contributing
     * projects; null when no project sidecars carried pm_dalys. */
    total_dalys?: number | null;
  };
  features: Array<{
    type: "Feature";
    geometry: GeoJSON.Geometry;
    properties: AggregateFeatureProperties;
  }>;
}

async function getAggregate(
  token: string,
  level: AggregationLevel,
  customerId?: string,
  filters?: Record<string, string>,
): Promise<AggregateFeatureCollection | null> {
  const params = new URLSearchParams({ level });
  if (customerId) params.set("customer_id", customerId);
  // With filters → on-the-fly filtered aggregation. Without → the
  // baked portfolio-wide geojson.
  const hasFilters = filters && Object.keys(filters).length > 0;
  if (hasFilters) {
    for (const [k, v] of Object.entries(filters!)) {
      params.set(`filter[${k}]`, v);
    }
  }
  const path = hasFilters
    ? "/portfolio/airshed-intensity-aggregate-filtered"
    : "/portfolio/airshed-intensity-aggregate";
  try {
    return await fetchWithAuth<AggregateFeatureCollection>(
      `${path}?${params.toString()}`,
      token,
    );
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("404") || msg.toLowerCase().includes("not been built")) return null;
    throw err;
  }
}

export function usePortfolioAggregate(
  level: AggregationLevel | null,
  customerId?: string,
  enabled = true,
  filters?: Record<string, string>,
) {
  const { token } = useAuth();
  const filterKey = filters ? JSON.stringify(filters) : "none";
  return useQuery<AggregateFeatureCollection | null>({
    queryKey: ["portfolio-aggregate", level ?? "none", customerId ?? "self", filterKey],
    queryFn: () => getAggregate(token!, level!, customerId, filters),
    enabled: Boolean(token) && Boolean(level) && enabled,
    staleTime: 30 * 60_000,
    gcTime: 2 * 60 * 60_000,
    retry: 0,
  });
}
